import {AnyImageOpHalData, ImageOpHalDataParameters} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {AddressMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {MipLevelRegion, Region, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {RegionDependencyGraph} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-dependency/region-dependency-graph"
import {applyWrapToRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-helpers"
import {BlitOutOfBoundsHandling, Definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blit/definition"
import {Box2, Size2Like, Vector2Like} from "@cm/math"
import {assertNever} from "@cm/utils/misc"
import {z} from "zod"

export const getBackgroundDependencyGraph = (args: {backgroundSize: Size2Like}): RegionDependencyGraph => {
    const dependency = new RegionDependencyGraph({
        sourceSize: args.backgroundSize,
        sourceAddressMode: "clamp",
        targetSize: args.backgroundSize,
        targetAddressMode: "clamp",
    })
    dependency.createStream().identity()
    return dependency
}

export const getForegroundDependencyGraph = (args: {
    backgroundSize: Size2Like
    foregroundSize: Size2Like
    foregroundRegion?: Region
    foregroundAddressMode: AddressMode
    position: Vector2Like
}): RegionDependencyGraph => {
    const dependency = new RegionDependencyGraph({
        sourceSize: args.foregroundSize,
        sourceAddressMode: args.foregroundAddressMode,
        targetSize: args.backgroundSize,
        targetAddressMode: "clamp",
    })
    dependency
        .createStream()
        .crop({
            x: args.foregroundRegion?.x ?? 0,
            y: args.foregroundRegion?.y ?? 0,
            width: args.foregroundRegion?.width ?? args.foregroundSize.width,
            height: args.foregroundRegion?.height ?? args.foregroundSize.height,
        })
        .offset({x: args.position.x, y: args.position.y})
    return dependency
}

export const determineBackgroundImageMipLevelRegion = (targetMipLevelRegion: MipLevelRegion): MipLevelRegion => {
    return {
        ...targetMipLevelRegion,
    }
}

export const determineForegroundImageMipLevelRegion = (
    foregroundImage: AnyImageOpHalData,
    foregroundRegion: Region | undefined,
    position: Vector2Like,
    targetMipLevelRegion: MipLevelRegion,
): MipLevelRegion => {
    const mipLevelScale = 1 / (1 << targetMipLevelRegion.mipLevel)
    foregroundRegion ??= {
        x: 0,
        y: 0,
        width: foregroundImage.descriptor.width,
        height: foregroundImage.descriptor.height,
    }
    const sourceP00 = {
        x: Math.floor(foregroundRegion.x * mipLevelScale),
        y: Math.floor(foregroundRegion.y * mipLevelScale),
    }
    const sourceP11 = {
        x: Math.ceil((foregroundRegion.x + foregroundRegion.width) * mipLevelScale),
        y: Math.ceil((foregroundRegion.y + foregroundRegion.height) * mipLevelScale),
    }
    const sourceRegion = Box2.intersect(
        {
            x: targetMipLevelRegion.x - position.x * mipLevelScale,
            y: targetMipLevelRegion.y - position.y * mipLevelScale,
            width: targetMipLevelRegion.width,
            height: targetMipLevelRegion.height,
        },
        {
            x: sourceP00.x,
            y: sourceP00.y,
            width: sourceP11.x - sourceP00.x,
            height: sourceP11.y - sourceP00.y,
        },
    ).expandToIntegersInPlace()
    return {
        ...sourceRegion,
        mipLevel: targetMipLevelRegion.mipLevel,
    }
}

export const determineTargetImageRegion = (
    parameters: ImageOpHalDataParameters<typeof Definition>,
    sourceImage: AnyImageOpHalData,
    sourceRegions: Regions,
): Regions => {
    switch (sourceImage) {
        case parameters.backgroundImage:
            return sourceRegions
        case parameters.foregroundImage: {
            const fullForegroundRegion = parameters.foregroundRegion ?? {
                x: 0,
                y: 0,
                width: parameters.foregroundImage.descriptor.width,
                height: parameters.foregroundImage.descriptor.height,
            }
            const regionsWithForegroundImage = getRegionsToDraw(
                parameters.outOfBoundsHandling,
                parameters.backgroundImage,
                {
                    ...parameters.position,
                    width: fullForegroundRegion.width,
                    height: fullForegroundRegion.height,
                },
                0,
            )
            // TODO optimize this
            return regionsWithForegroundImage
            // regionsWithForegroundImage.forEach((region) => {
            //     console.log("Invalidating region with foreground image", region)
            //     invalidateRegion(region)
            //     // const sourceRegionForThisRegion = {
            //     //     x: region.x - parameters.position.x + fullForegroundRegion.x,
            //     //     y: region.y - parameters.position.y + fullForegroundRegion.y,
            //     //     width: region.width,
            //     //     height: region.height,
            //     // }
            //     // const foregroundRegions = applyBorderModeToRegion(
            //     //     parameters.foregroundImage.descriptor,
            //     //     sourceRegionForThisRegion,
            //     //     0,
            //     //     parameters.foregroundAddressMode,
            //     // )
            //     // foregroundRegions.forEach((foregroundRegion) => {
            //     //     const invalidatedForegroundRegion = Box2.intersect(foregroundRegion, sourceRegion)
            //     //     if (!invalidatedForegroundRegion.isEmpty()) {
            //     //         const regionToInvalidate = {
            //     //             x: invalidatedForegroundRegion.x - fullForegroundRegion.x + parameters.position.x,
            //     //             y: invalidatedForegroundRegion.y - parameters.position.y,
            //     //             width: invalidatedForegroundRegion.width,
            //     //             height: invalidatedForegroundRegion.height,
            //     //         }
            //     //         invalidateRegion(inva)
            //     //     }
            //     // })
            // })
        }
        default:
            throw new Error("Invalid source image")
    }
}

export const getRegionsToDraw = (
    outOfBoundsHandling: z.infer<typeof BlitOutOfBoundsHandling>,
    backgroundImage: AnyImageOpHalData,
    regionToDraw: Region,
    mipLevel: number,
): Regions => {
    switch (outOfBoundsHandling) {
        case "clip":
            return [regionToDraw]
        case "wrap":
            return applyWrapToRegion(backgroundImage.descriptor, regionToDraw, mipLevel)
        default:
            assertNever(outOfBoundsHandling)
    }
}
